import { useState } from 'react';
import {
  CheckCircle2,
  ExternalLink,
  FileText,
  Loader2,
  XCircle,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CameraCapture } from './CameraCapture';
import { useSaveRemont } from '../hooks/useRemont';
import { clientsApi } from '@/api/clients';
import { serviceCenterRepairsApi } from '@/api/service-center-repairs';
import type {
  RemontEntry,
  SaveRemontDto,
  Vendor,
} from '@/api/service-center-repairs';

interface RemontSheetProps {
  entry: Partial<RemontEntry> | null;
  vendors: Vendor[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const attachmentLabels = {
  DEVICE_BEFORE: 'Фото до ремонта',
  DEVICE_AFTER: 'Фото после ремонта',
  SERVICE_INVOICE: 'Счёт СЦ',
  SERVICE_ACT: 'Акт СЦ',
  OTHER: 'Другой файл',
} as const;

async function openAttachment(repairId: string, attachmentId: string) {
  const url = await serviceCenterRepairsApi.getAttachmentBlobUrl(
    repairId,
    attachmentId,
  );
  window.open(url, '_blank', 'noopener,noreferrer');
  window.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

function BackofficeStatus({
  label,
  done,
  doneLabel,
  pendingLabel,
}: {
  label: string;
  done: boolean;
  doneLabel: string;
  pendingLabel: string;
}) {
  const Icon = done ? CheckCircle2 : XCircle;
  const statusLabel = done ? doneLabel : pendingLabel;

  return (
    <div
      className="flex items-center justify-between gap-3"
      aria-label={`${label}: ${statusLabel}`}
    >
      <span className="text-foreground">{label}</span>
      <span
        className={`inline-flex items-center gap-1.5 font-medium ${
          done ? 'text-emerald-700' : 'text-red-600'
        }`}
      >
        <Icon className="h-4 w-4" aria-hidden="true" />
        {statusLabel}
      </span>
    </div>
  );
}

export function RemontSheet({ entry, vendors, open, onOpenChange }: RemontSheetProps) {
  const saveRemont = useSaveRemont();

  // Fetch clients for selector
  const { data: clients = [] } = useQuery({
    queryKey: ['clients-for-selector'],
    queryFn: () => clientsApi.getForSelector('all'),
  });

  const isEditing = Boolean(entry?.id);
  const repairId = entry?.id;
  const attachments = entry?.raw?.attachments ?? [];
  const initialCompany = entry?.company || '';

  // Form state
  const [company, setCompany] = useState(initialCompany);
  const [clientId, setClientId] = useState<number | undefined>(
    entry?.clientId ?? undefined,
  );
  const [isManualEntry, setIsManualEntry] = useState(
    !entry?.clientId && initialCompany !== '',
  );
  const [whose, setWhose] = useState(entry?.whose || '');
  const [name, setName] = useState(entry?.name || '');
  const [vendorId, setVendorId] = useState<string>(entry?.vendor_id || '');
  const [comment, setComment] = useState(entry?.comment || '');
  const [otvez, setOtvez] = useState(Number(entry?.otvez) === 1);
  const [cost, setCost] = useState(entry?.cost || '');
  const [zabral, setZabral] = useState(Number(entry?.zabral) === 1);
  const [answer, setAnswer] = useState(entry?.answer || '');
  const [photo, setPhoto] = useState<Blob | null>(null);

  const handleSave = () => {
    if (!name.trim() || !company.trim()) return;

    const dto: SaveRemontDto = {
      id: entry?.id,
      clientId: isManualEntry ? undefined : clientId,
      name: name.trim(),
      company: company.trim(),
      whose: whose.trim(),
      comment: comment.trim(),
      vendor_id: vendorId,
      otvez: otvez ? 1 : 0,
      zabral: zabral ? 1 : 0,
      estimatedVendorAmount: cost.trim() ? Number(cost) : undefined,
      answer: answer.trim(),
    };

    saveRemont.mutate(
      { dto, photo: photo || undefined },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      }
    );
  };

  const canSave = name.trim() && company.trim() && !saveRemont.isPending;
  const costNum = parseFloat(cost) || 0;
  const pricingPreview = useQuery({
    queryKey: ['service-center-repair-pricing-preview', costNum],
    queryFn: () => serviceCenterRepairsApi.pricingPreview(costNum),
    enabled: costNum > 0,
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[90vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {isEditing ? 'Редактирование заявки' : 'Новая заявка на ремонт'}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-4 space-y-4 pb-4">
          {/* Client */}
          <div>
            <Label>Клиент *</Label>
            <Select
              value={isManualEntry ? '__manual__' : clientId ? String(clientId) : ''}
              onValueChange={(val) => {
                if (val === '__manual__') {
                  setIsManualEntry(true);
                  setClientId(undefined);
                  setCompany('');
                } else {
                  const selectedClient = clients.find((c) => c.id === Number(val));
                  setIsManualEntry(false);
                  setClientId(selectedClient?.id);
                  setCompany(selectedClient?.name || '');
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Выберите клиента" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    {c.name}
                  </SelectItem>
                ))}
                <SelectItem value="__manual__">Ввести вручную</SelectItem>
              </SelectContent>
            </Select>
            {isManualEntry && (
              <Input
                className="mt-2"
                placeholder="Название клиента"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
              />
            )}
          </div>

          {/* Whose device */}
          <div>
            <Label htmlFor="whose">Чьё устройство</Label>
            <Input
              id="whose"
              placeholder="ФИО сотрудника"
              value={whose}
              onChange={(e) => setWhose(e.target.value)}
            />
          </div>

          {/* Device name */}
          <div>
            <Label htmlFor="name">Устройство *</Label>
            <Input
              id="name"
              placeholder="Марка, модель"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* Service center */}
          <div>
            <Label>Сервис-центр</Label>
            <Select value={vendorId} onValueChange={setVendorId}>
              <SelectTrigger>
                <SelectValue placeholder="Выберите СЦ" />
              </SelectTrigger>
              <SelectContent>
                {vendors.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Problem description */}
          <div>
            <Label htmlFor="comment">Неисправность</Label>
            <Textarea
              id="comment"
              placeholder="Описание проблемы"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
            />
          </div>

          {/* Photo */}
          <div>
            <Label>Фото устройства</Label>
            <CameraCapture
              existingPhoto={entry?.photo}
              onCapture={setPhoto}
            />
            {repairId && attachments.length > 0 && (
              <div className="mt-2 divide-y rounded-md border">
                {attachments.map((attachment) => (
                  <button
                    key={attachment.id}
                    type="button"
                    onClick={() => openAttachment(repairId, attachment.id)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm"
                  >
                    <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate">
                      {attachment.originalName}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {attachmentLabels[attachment.kind]}
                    </span>
                    <ExternalLink className="h-4 w-4 shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Status section */}
          <div className="border-t pt-4">
            <p className="text-sm font-medium mb-3">Статус</p>

            {/* Delivered */}
            <div className="flex items-center space-x-2 mb-3">
              <Checkbox
                id="otvez"
                checked={otvez}
                onCheckedChange={(c) => setOtvez(c === true)}
              />
              <Label htmlFor="otvez" className="font-normal">Отвёз в СЦ</Label>
            </div>

            {/* Cost */}
            <div className="mb-3">
              <Label htmlFor="cost">Предварительная оценка СЦ</Label>
              <Input
                id="cost"
                type="number"
                placeholder="Сумма в рублях"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
              />
              {pricingPreview.data && (
                <div className="mt-2 flex items-baseline justify-between gap-3 rounded-md border border-primary/20 bg-primary/5 px-3 py-2">
                  <span className="text-sm font-medium">Назвать клиенту</span>
                  <strong className="whitespace-nowrap text-lg text-primary">
                    {pricingPreview.data.calculatedClientTotal} BYN
                  </strong>
                </div>
              )}
            </div>

            {/* Picked up */}
            <div className="flex items-center space-x-2 mb-3">
              <Checkbox
                id="zabral"
                checked={zabral}
                onCheckedChange={(c) => setZabral(c === true)}
              />
              <Label htmlFor="zabral" className="font-normal">Забрал из СЦ</Label>
            </div>

            {/* Service center answer */}
            <div>
              <Label htmlFor="answer">Ответ СЦ</Label>
              <Input
                id="answer"
                placeholder="Что сделали / диагноз"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
              />
            </div>
          </div>

          {/* Backoffice status */}
          <div className="border-t pt-4">
            <p className="text-sm font-medium mb-3">Backoffice</p>
            <div className="space-y-2 text-sm">
              <BackofficeStatus
                label="Документы СЦ"
                done={Boolean(entry?.raw?.documentsReceivedFromService)}
                doneLabel="Получены"
                pendingLabel="Не получены"
              />
              <BackofficeStatus
                label="Счёт клиенту"
                done={Boolean(entry?.raw?.billedToClient)}
                doneLabel="Выставлен"
                pendingLabel="Не выставлен"
              />
              <BackofficeStatus
                label="Оплата СЦ"
                done={Boolean(entry?.raw?.paidToService)}
                doneLabel="Оплачен"
                pendingLabel="Не оплачен"
              />
              <BackofficeStatus
                label="Архив"
                done={Boolean(entry?.raw?.isArchived)}
                doneLabel="В архиве"
                pendingLabel="Не в архиве"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="border-t pt-4 space-y-2">
            <Button
              onClick={handleSave}
              disabled={!canSave}
              className="w-full"
            >
              {saveRemont.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Сохранение...
                </>
              ) : (
                'Сохранить'
              )}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
